import React, { useState, useEffect, useCallback } from 'react';
import { Search, FileText, ChevronDown, ChevronUp, Clock, CheckCircle, XCircle, Key, Cpu } from 'lucide-react';
import { logsService } from '../services/api';

const PAGE_SIZE = 20;

function fmtDate(ts) {
  if (!ts) return '-';
  return new Date(ts).toLocaleString();
}

function fmtMs(ms) {
  if (!ms) return '-';
  return ms < 1000 ? ms + 'ms' : (ms / 1000).toFixed(1) + 's';
}

function parseMessages(text) {
  try { return JSON.parse(text); } catch { return null; }
}

function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-2`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap break-words
        ${isUser
          ? 'bg-indigo-600 text-white rounded-br-sm'
          : 'bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-100 rounded-bl-sm'}`}>
        <span className="block text-[10px] font-semibold opacity-60 mb-1 uppercase tracking-wide">
          {msg.role}
        </span>
        {typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content)}
      </div>
    </div>
  );
}

function LogRow({ log }) {
  const [open, setOpen] = useState(false);
  const messages = parseMessages(log.prompt_text);

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
      {/* Summary row */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left"
      >
        <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center
          ${log.status === 'success' ? 'text-emerald-500' : 'text-red-500'}`}>
          {log.status === 'success' ? <CheckCircle size={16}/> : <XCircle size={16}/>}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
            {log.key_name || log.key_id}
          </span>
          <span className="block text-xs text-slate-400 truncate">
            {log.model_name}
          </span>
        </span>
        <span className="hidden sm:block text-xs text-slate-400 flex-shrink-0 w-28 text-right">
          {fmtDate(log.created_at)}
        </span>
        <span className="hidden sm:flex items-center gap-3 text-xs text-slate-500 flex-shrink-0 w-32 justify-end">
          <span>{log.input_tokens}↑ {log.output_tokens}↓</span>
          <span>{fmtMs(log.response_time_ms)}</span>
        </span>
        {open ? <ChevronUp size={14} className="text-slate-400 flex-shrink-0"/> : <ChevronDown size={14} className="text-slate-400 flex-shrink-0"/>}
      </button>

      {/* Expanded detail */}
      {open && (
        <div className="border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-4">
          <div className="flex flex-wrap gap-3 text-xs text-slate-500 mb-4">
            <span className="flex items-center gap-1"><Key size={11}/> {log.key_name}</span>
            <span className="flex items-center gap-1"><Cpu size={11}/> {log.model_name}</span>
            <span className="flex items-center gap-1"><Clock size={11}/> {fmtDate(log.created_at)}</span>
            <span>Input: {log.input_tokens} tokens</span>
            <span>Output: {log.output_tokens} tokens</span>
            <span>Time: {fmtMs(log.response_time_ms)}</span>
          </div>

          {/* Chat-style prompt */}
          {messages && Array.isArray(messages) ? (
            <div className="mb-4">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Prompt</p>
              <div className="bg-white dark:bg-slate-800 rounded-xl p-3 border border-slate-200 dark:border-slate-700">
                {messages.map((m, i) => <MessageBubble key={i} msg={m}/>)}
              </div>
            </div>
          ) : log.prompt_text ? (
            <div className="mb-4">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Prompt</p>
              <pre className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3 text-sm whitespace-pre-wrap break-words">{log.prompt_text}</pre>
            </div>
          ) : null}

          {/* Response */}
          {log.status === 'success' && log.response_text ? (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Response</p>
              <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3">
                <div className="flex justify-start mb-1">
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">assistant</span>
                </div>
                <p className="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap break-words">{log.response_text}</p>
              </div>
            </div>
          ) : log.status === 'error' ? (
            <div>
              <p className="text-xs font-semibold text-red-400 uppercase tracking-wide mb-2">Error</p>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl px-4 py-3 text-sm text-red-700 dark:text-red-300">
                {log.error_message || 'Unknown error'}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default function Logs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await logsService.getLogs({
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        search,
      });
      setLogs(res.data.rows || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Request Logs</h1>
          <p className="text-sm text-slate-500 mt-0.5">{total.toLocaleString()} total requests logged</p>
        </div>
        <div className="flex items-center gap-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2">
          <Search size={14} className="text-slate-400"/>
          <input
            className="bg-transparent text-sm outline-none w-48 placeholder-slate-400 dark:text-white"
            placeholder="Search key, model…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); } }}
          />
        </div>
      </div>

      {/* Log list */}
      {loading ? (
        <div className="flex items-center justify-center h-48 text-slate-400">Loading…</div>
      ) : logs.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 text-slate-400 gap-2">
          <FileText size={32} className="opacity-30"/>
          <p className="text-sm">No logs yet. Make an API call to see it here.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {logs.map(log => <LogRow key={log.id} log={log}/>)}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 text-sm text-slate-500">
          <span>Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
              Previous
            </button>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
              className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
